import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart, BarChartTickXFormats, SingleValueBarFormat, TickY, TickX } from '@charts';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { formatSamplesViewGraph } from '@modules/study-insights/utils';

import { ViewGraphFilterProps } from './SampleViewGraphFilter/SampleViewGraphFilter.types';
import { SampleViewGraphProps } from './SampleViewGraph.types';
import { StyledCard } from './SampleViewGraph.styles';

import SampleViewGraphFilter from './SampleViewGraphFilter/SampleViewGraphFilter';
import { CardVariants } from '@/common/components';
import { PillId } from '../../CollectionAndSequencing.types';

const tickY: TickY = { min: 0, unit: { symbol: '' } };
const tickX: TickX = {
  transformLabelFn: BarChartTickXFormats.label.labelSplitBySpaces,
  transformLabelCssFn: BarChartTickXFormats.css.cssBoldFirstLine,
};

const SampleViewGraph: FC<SampleViewGraphProps> = ({
  displaySampleTypeFilter = false,
  selectedDateRange,
  selectedPill,
  data,
  onFilterChange,
  sampleTypeFilterText,
  excludedSampleTypes,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [formattedData, setFormattedData] = useState<SingleValueBarFormat[]>();

  const handleChangeFilter = (filter: ViewGraphFilterProps): void => {
    onFilterChange(filter);
  };

  const getTooltipTitle = (selectedPill: string): string => {
    return selectedPill === PillId.SEQUENCED
      ? t(`samples_sequenced.samples_sequenced`)
      : t(`received_samples.samples_received`);
  };

  useEffect(() => {
    if (!data || !selectedDateRange) {
      return;
    }

    const formatted = formatSamplesViewGraph(data, selectedDateRange);

    setFormattedData(formatted);
  }, [data]);

  return (
    <>
      <SampleViewGraphFilter
        selectedDateRange={selectedDateRange}
        onFilterChange={handleChangeFilter}
        displaySampleTypeFilter={displaySampleTypeFilter}
        sampleTypeFilterText={sampleTypeFilterText}
        excludedSampleTypes={excludedSampleTypes}
      />
      <StyledCard variant={CardVariants.TERTIARY}>
        {formattedData && (
          <BarChart
            data={formattedData}
            tickX={tickX}
            tickY={tickY}
            tooltip={getTooltipTitle(selectedPill)}
            startAtEnd={false}
          />
        )}
      </StyledCard>
    </>
  );
};

export default SampleViewGraph;
